import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError } from "../api/client";
import { useJob } from "../api/jobs";
import { useAssetTranscription, useAssets, useTranscribe, useTranscriptionFailures, useTranscriptionWatcher } from "../api/queries";
import type { MoodleAsset, TranscriptionFailureGroup, TranscriptionStatus } from "../api/types";
import { JobNotice } from "../components/JobNotice";
import { Button, Card, Empty, ErrorBanner, Loading, PageHeader, RelativeTime, StatusBadge } from "../components/ui";
import { FilterIcon } from "../components/icons";
import { PageNav } from "../components/PageNav";
import { bytes } from "../lib/format";

const PAGE_SIZE = 50;

// "Ready" is the API's "pending": media never attempted (or not yet started).
const CHIPS: { status: TranscriptionStatus | undefined; label: string; dot: string }[] = [
  { status: undefined, label: "All", dot: "bg-gray-400" },
  { status: "failed", label: "Failed", dot: "bg-red-500" },
  { status: "completed", label: "Completed", dot: "bg-green-500" },
  { status: "pending", label: "Ready", dot: "bg-amber-400" },
];

// Audio/video assets and their transcription state (GET /moodle/assets?media=true),
// with per-file and bulk transcribe/retry, the transcript drawer, and the
// scheduled watcher's state. ?status=pending|failed|completed narrows the list;
// "failed" shows failures grouped by error instead of one row per file.
export function TranscriptionsPage() {
  const [params, setParams] = useSearchParams();
  const statusParam = params.get("status");
  const status = CHIPS.find((c) => c.status === statusParam)?.status;
  const [offset, setOffset] = useState(0);
  const [viewing, setViewing] = useState<MoodleAsset | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const grouped = status === "failed";
  const assets = useAssets({ status, offset, limit: PAGE_SIZE });
  const watcher = useTranscriptionWatcher();
  const transcribe = useTranscribe();
  const job = useJob("transcribe");
  const counts = watcher.data?.counts;
  const rows = assets.data?.assets ?? [];
  const busy = transcribe.isPending || !!job;
  // Only files that can be (re)transcribed are selectable.
  const selectable = rows.filter((a) => canTranscribe(a));
  const allOnPageSelected = selectable.length > 0 && selectable.every((a) => selected.has(a.id));

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const pick = (next: TranscriptionStatus | undefined) => {
    setOffset(0);
    setSelected(new Set());
    setParams(next ? { status: next } : {});
  };

  const changePage = (nextOffset: number) => {
    setOffset(nextOffset);
    setSelected(new Set());
  };

  return (
    <div className="p-6">
      <PageHeader
        title="Transcriptions"
        subtitle="Audio and video pulled from Moodle, transcribed into timestamped documents that go to the approval queue."
      />

      <JobNotice kind="transcribe" />
      <ErrorBanner error={transcribe.error} />
      {job && (
        <p className="mb-4 text-sm text-blue-700">Transcription job {job.state === "PENDING" ? "queued" : "running"}…</p>
      )}

      <section className="rounded-xl border border-gray-200 bg-white">
        {!grouped && !!counts?.failed && (
          <div className="flex items-center gap-2 px-4 pt-3 text-sm">
            <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
            <span className="text-red-700">
              {counts.failed.toLocaleString()} failing {counts.failed === 1 ? "file" : "files"}
            </span>
            <button type="button" onClick={() => pick("failed")} className="font-medium text-brand hover:underline">
              Review
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 px-4 py-3">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Transcription status">
            {CHIPS.map((chip) => {
              const active = chip.status === status;
              const count = counts ? (chip.status ? counts[chip.status] : counts.total) : undefined;
              return (
                <button
                  key={chip.label}
                  role="tab"
                  aria-selected={active}
                  onClick={() => pick(chip.status)}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                    active ? "border-brand bg-brand text-white" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className={`h-2 w-2 rounded-full ${active ? "bg-white/70" : chip.dot}`} />
                  {chip.label}
                  {count !== undefined && (
                    <span className={`tabular-nums ${active ? "text-white/75" : "text-gray-400"}`}>{count.toLocaleString()}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="ml-auto">
            <ActionsMenu>
              {(close) => (
                <>
                  <MenuItem
                    disabled={busy || !selected.size}
                    loading={transcribe.isPending && (transcribe.variables?.assetIds?.length ?? 0) > 1}
                    onClick={() => {
                      transcribe.mutate({ assetIds: [...selected] }, { onSuccess: () => setSelected(new Set()) });
                      close();
                    }}
                  >
                    Transcribe selected ({selected.size})
                  </MenuItem>
                  <MenuItem
                    disabled={busy || !counts?.pending}
                    loading={transcribe.isPending && transcribe.variables?.only === "pending"}
                    onClick={() => {
                      transcribe.mutate({ only: "pending" });
                      close();
                    }}
                  >
                    Transcribe all pending{counts ? ` (${counts.pending})` : ""}
                  </MenuItem>
                  <MenuItem
                    disabled={busy || !counts?.failed}
                    loading={transcribe.isPending && transcribe.variables?.only === "failed"}
                    onClick={() => {
                      transcribe.mutate({ only: "failed" });
                      close();
                    }}
                  >
                    Retry all failed{counts ? ` (${counts.failed})` : ""}
                  </MenuItem>
                </>
              )}
            </ActionsMenu>
          </div>
        </div>

        {grouped ? (
          <div className="border-t border-gray-100 p-4">
            <FailureGroups busy={busy} transcribe={transcribe} />
          </div>
        ) : (
          <>
            {assets.error && (
              <div className="px-4 pt-4">
                <ErrorBanner error={assets.error} />
              </div>
            )}
            {assets.isLoading ? (
              <div className="p-4">
                <Loading />
              </div>
            ) : !rows.length ? (
              <div className="p-4">
                <Empty>{emptyMessage(status)}</Empty>
              </div>
            ) : (
              <div className="overflow-x-auto border-t border-gray-100">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/70 text-left">
                      <th className="w-10 px-4 py-3">
                        <input
                          type="checkbox"
                          aria-label="Select page"
                          disabled={!selectable.length}
                          checked={allOnPageSelected}
                          onChange={() => setSelected(allOnPageSelected ? new Set() : new Set(selectable.map((a) => a.id)))}
                        />
                      </th>
                      <th className="px-4 py-3 font-semibold text-gray-700">Document</th>
                      <th className="px-4 py-3 font-semibold text-gray-700">Course / section</th>
                      <th className="px-4 py-3 font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-3 font-semibold text-gray-700">Error</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((a) => (
                      <tr key={a.id} className="align-top transition hover:bg-gray-50/70">
                        <td className="w-10 px-4 py-3">
                          {canTranscribe(a) && (
                            <input
                              type="checkbox"
                              aria-label={`Select ${a.filename}`}
                              checked={selected.has(a.id)}
                              onChange={() => toggle(a.id)}
                            />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium break-all text-brand">{a.filename}</span>
                          <div className="mt-0.5 text-xs text-gray-500">{a.kind} · {bytes(a.filesize)} · asset {a.id}</div>
                        </td>
                        <td className="px-4 py-3">
                          {a.course_moodle_id !== null ? (
                            <Link to={`/courses/${a.course_moodle_id}`} className="hover:underline">{a.course_fullname ?? `Course ${a.course_moodle_id}`}</Link>
                          ) : <span className="text-gray-400">—</span>}
                          {a.module_name && (
                            <div className="mt-0.5 text-xs text-gray-500">{a.module_name}{a.module_cmid !== null && ` · cmid ${a.module_cmid}`}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={a.transcription_status ?? "pending"} label={stateLabel(a.transcription_status)} />
                          {!!a.transcription_attempts && a.transcription_attempts > 1 && (
                            <div className="mt-1 text-xs text-gray-500">{a.transcription_attempts} attempts</div>
                          )}
                        </td>
                        <td className={`max-w-xs px-4 py-3 text-xs ${a.transcription_status === "failed" ? "text-red-700" : "text-gray-500"}`}>
                          <div className="truncate" title={a.transcription_error ?? undefined}>{a.transcription_error}</div>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <RowAction asset={a} busy={busy} transcribe={transcribe} onView={() => setViewing(a)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!!rows.length && (
              <div className="border-t border-gray-100">
                <PageNav offset={offset} limit={PAGE_SIZE} total={assets.data?.total} onChange={changePage} />
              </div>
            )}
          </>
        )}
      </section>

      {viewing && <TranscriptDrawer asset={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

// The dark toolbar button from the design: holds the bulk transcribe actions so
// the header stays title-only. Closes on outside click or Escape.
function ActionsMenu({ children }: { children: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Transcription actions"
        onClick={() => setOpen((v) => !v)}
        className="grid h-9 w-9 place-items-center rounded-md bg-brand text-white transition hover:bg-brand-dark"
      >
        <FilterIcon className="h-4 w-4" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-20 mt-1 w-60 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

function MenuItem({ disabled, loading, onClick, children }: {
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled || loading}
      onClick={onClick}
      className="block w-full px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {loading ? "Working…" : children}
    </button>
  );
}

// The API caps one transcribe request at this many asset ids.
const MAX_RETRY_IDS = 500;

// One card per distinct error: the message once, how many files it hit and
// how often they were tried in total, with a retry for the whole group.
function FailureGroups({ busy, transcribe }: { busy: boolean; transcribe: ReturnType<typeof useTranscribe> }) {
  const failures = useTranscriptionFailures();
  const groups = failures.data?.groups ?? [];

  if (failures.error) return <ErrorBanner error={failures.error} />;
  if (failures.isLoading) return <Loading />;
  if (!groups.length) return <Empty>{emptyMessage("failed")}</Empty>;
  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <FailureGroupCard
          key={g.error}
          group={g}
          busy={busy}
          transcribe={transcribe}
          // Retrying the only group is the same as retrying every failure.
          onlyGroup={groups.length === 1}
        />
      ))}
    </div>
  );
}

function FailureGroupCard({ group, busy, transcribe, onlyGroup }: {
  group: TranscriptionFailureGroup;
  busy: boolean;
  transcribe: ReturnType<typeof useTranscribe>;
  onlyGroup: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ids = group.files.map((f) => f.id);
  const scope = onlyGroup ? { only: "failed" as const } : { assetIds: ids.slice(0, MAX_RETRY_IDS) };
  const mine = transcribe.isPending && (onlyGroup
    ? transcribe.variables?.only === "failed"
    : transcribe.variables?.assetIds?.[0] === ids[0]);
  const files = `${group.file_count.toLocaleString()} ${group.file_count === 1 ? "file" : "files"}`;

  return (
    <Card
      title={files}
      actions={
        <>
          <Button onClick={() => setOpen(!open)} aria-expanded={open}>{open ? "Hide files" : "Show files"}</Button>
          <Button variant="brand" loading={mine} disabled={busy} onClick={() => transcribe.mutate(scope)}>
            {group.file_count === 1 ? "Retry" : `Retry these ${group.file_count.toLocaleString()}`}
          </Button>
        </>
      }
    >
      <p className="break-words text-sm text-red-700">{group.error}</p>
      <p className="mt-2 text-xs text-gray-500">
        Tried {group.attempts.toLocaleString()} {group.attempts === 1 ? "time" : "times"} in total
        {group.last_failed_at && <> · last failed <RelativeTime iso={group.last_failed_at} /></>}
        {!onlyGroup && group.file_count > MAX_RETRY_IDS && ` · retry sends the first ${MAX_RETRY_IDS}`}
      </p>
      {open && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/70 text-left">
                <th className="px-4 py-3 font-semibold text-gray-700">File</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Course / section</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Tries</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {group.files.map((f) => (
                <tr key={f.id} className="align-top">
                  <td className="px-4 py-3">
                    <span className="font-medium break-all text-brand">{f.filename}</span>
                    <div className="mt-0.5 text-xs text-gray-500">{f.kind} · {bytes(f.filesize)} · asset {f.id}</div>
                  </td>
                  <td className="px-4 py-3">
                    {f.course_moodle_id !== null ? (
                      <Link to={`/courses/${f.course_moodle_id}`} className="hover:underline">{f.course_fullname ?? `Course ${f.course_moodle_id}`}</Link>
                    ) : <span className="text-gray-400">—</span>}
                    {f.module_name && <div className="mt-0.5 text-xs text-gray-500">{f.module_name}{f.module_cmid !== null && ` · cmid ${f.module_cmid}`}</div>}
                  </td>
                  <td className="px-4 py-3">{f.attempts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function canTranscribe(asset: MoodleAsset) {
  const status = asset.transcription_status;
  return status !== "completed" && status !== "processing" && status !== "skipped";
}

function stateLabel(status: string | null) {
  if (!status || status === "pending") return "ready";
  return status;
}

function emptyMessage(status: TranscriptionStatus | undefined) {
  switch (status) {
    case "pending": return "Nothing waiting to be transcribed.";
    case "failed": return "No failed transcriptions.";
    case "completed": return "No completed transcriptions yet.";
    default: return "No audio or video has been extracted from Moodle yet.";
  }
}

function RowAction({ asset, busy, transcribe, onView }: {
  asset: MoodleAsset;
  busy: boolean;
  transcribe: ReturnType<typeof useTranscribe>;
  onView: () => void;
}) {
  const status = asset.transcription_status;
  const mine = transcribe.isPending && transcribe.variables?.assetIds?.length === 1 && transcribe.variables.assetIds[0] === asset.id;
  if (status === "completed") {
    return (
      <button type="button" onClick={onView} className="text-sm font-medium text-brand hover:underline">
        View transcript
      </button>
    );
  }
  if (status === "processing") return <span className="text-xs text-blue-700">Transcribing…</span>;
  if (status === "skipped") {
    return (
      <button type="button" onClick={onView} className="text-sm font-medium text-brand hover:underline">
        Details
      </button>
    );
  }
  return (
    <Button
      variant={status === "failed" ? "secondary" : "brand"}
      loading={mine}
      disabled={busy}
      onClick={() => transcribe.mutate({ assetIds: [asset.id] })}
    >
      {status === "failed" ? "Retry" : "Transcribe"}
    </Button>
  );
}

function timestamp(seconds: number | null | undefined) {
  if (seconds === null || seconds === undefined) return "";
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

function TranscriptDrawer({ asset, onClose }: { asset: MoodleAsset; onClose: () => void }) {
  const detail = useAssetTranscription(asset.id);
  const t = detail.data?.transcription;
  const documents = detail.data?.documents ?? [];
  const missing = detail.error instanceof ApiError && detail.error.status === 404;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Transcript of ${asset.filename}`}
        className="flex h-full w-full max-w-xl flex-col bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-2 border-b px-4 py-3">
          <div className="min-w-0">
            <h2 className="font-semibold break-all">{asset.filename}</h2>
            <div className="text-xs text-gray-500">{asset.module_name ?? `asset ${asset.id}`}</div>
          </div>
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-900" aria-label="Close">✕</button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 text-sm">
          {detail.isLoading ? <Loading /> : missing ? (
            <Empty>This file has no transcription yet.</Empty>
          ) : detail.error ? (
            <ErrorBanner error={detail.error} />
          ) : t ? (
            <>
              <dl className="mb-4 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <dt className="text-gray-500">Status</dt><dd><StatusBadge status={t.status} /></dd>
                <dt className="text-gray-500">Model</dt><dd>{t.model} <span className="text-gray-500">({t.model_version})</span></dd>
                <dt className="text-gray-500">Language</dt><dd>{t.language ?? "—"}</dd>
                <dt className="text-gray-500">Duration</dt><dd>{t.duration_seconds !== null ? timestamp(t.duration_seconds) : "—"}</dd>
                <dt className="text-gray-500">Attempts</dt><dd>{t.attempts}</dd>
                <dt className="text-gray-500">Finished</dt><dd><RelativeTime iso={t.finished_at} /></dd>
              </dl>
              {t.error && <p className="mb-4 break-words text-xs text-red-700">{t.error}</p>}

              <h3 className="mb-2 font-medium">Transcript</h3>
              {t.segments.length ? (
                <ol className="space-y-2">
                  {t.segments.map((seg, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="w-14 shrink-0 font-mono text-xs text-gray-500 pt-0.5">{timestamp(seg.start)}</span>
                      <span>{seg.text}</span>
                    </li>
                  ))}
                </ol>
              ) : t.text ? (
                <p className="whitespace-pre-wrap">{t.text}</p>
              ) : (
                <Empty>No transcript text.</Empty>
              )}

              {documents.length > 0 && (
                <>
                  <h3 className="mt-6 mb-2 font-medium">Generated documents</h3>
                  <ul className="divide-y rounded border">
                    {documents.map((d) => (
                      <li key={d.id} className="flex items-center justify-between gap-2 px-3 py-2">
                        <span className="min-w-0 truncate" title={d.title ?? undefined}>
                          <span className="font-mono text-xs">{timestamp(d.media_start_seconds)}–{timestamp(d.media_end_seconds)}</span>
                          <span className="ml-2 text-xs text-gray-500">document {d.id}</span>
                        </span>
                        <StatusBadge status={d.approval_status} />
                      </li>
                    ))}
                  </ul>
                  {documents.some((d) => d.approval_status === "pending") && (
                    <p className="mt-2 text-xs text-gray-500">
                      Pending documents aren't searchable until approved in the <Link to="/approvals" className="underline">approval queue</Link>.
                    </p>
                  )}
                </>
              )}
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );
}
